import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as cameraActions from './cameras.actions';
import { of } from 'rxjs';
import { DataApiService } from '../../services/data/data-api.service';
import * as copActions from '../cop/cop.actions';
import { VideoStreamType } from '../../../core/store/models/camera-with-media.model';
import { DatafeedApiService } from '../../../management/store/datafeeds/datafeed-api.service';
import { MapDataWorkerService } from '../../web-workers/client/map-data-worker.service';
import { VideoStreamService } from '../../../core/services/video-stream.service';

@Injectable()
export class CamerasEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private dataApiService: DataApiService,
    private pipelineApiService: DatafeedApiService,
    private mapDataWorkerService: MapDataWorkerService,
    private videoStreamService: VideoStreamService
  ) {}

  getTrafficStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cameraActions.getTrafficStream),
      switchMap(action => {
        return this.dataApiService.getTrafficStream(action.cameraId, action.width, action.height, action.coding, action.container).pipe(
          map(res => cameraActions.loadTrafficStream({ streamUrl: res.mediaUrl })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  getTrafficCameraImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cameraActions.getTrafficCameraImage),
      switchMap(action => {
        return this.dataApiService.getTrafficImage(action.cameraId).pipe(
          map(res => cameraActions.loadTrafficCameraImage({ imageUrl: res.mediaUrl })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  refreshVideoStream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cameraActions.refreshVideoStream),
      switchMap(action => {
        if (action.videoStream.type == VideoStreamType.Traffic) {
          if (action.videoStream.videoUrl) {
            return this.dataApiService
              .getTrafficStream(
                action.videoStream.cameraId,
                action.videoStream.width,
                action.videoStream.height,
                action.videoStream.coding,
                action.videoStream.container
              )
              .pipe(
                map(res => {
                  action.videoStream.videoUrl = res.mediaUrl;
                  return cameraActions.refreshVideoStreamComplete({ videoStream: action.videoStream });
                }),
                catchError(err => of(copActions.setError({ err })))
              );
          } else {
            return this.dataApiService.getTrafficImage(action.videoStream.cameraId).pipe(
              map(res => {
                action.videoStream.imageUrl = res.mediaUrl;
                return cameraActions.refreshVideoStreamComplete({ videoStream: action.videoStream });
              }),
              catchError(err => of(copActions.setError({ err })))
            );
          }
        } else {
          return [cameraActions.refreshVideoStreamComplete({ videoStream: action.videoStream })];
        }
      })
    )
  );

  getOpenEyeCameraStreamUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cameraActions.getOpenEyeCameraStreamUrl),
      switchMap(action => {
        return this.pipelineApiService
          .getOpenEyeCameraStreamUrl(action.companyId, action.deviceId, action.cameraId, action.streamName, action.pipelineDefinitionUId)
          .pipe(
            map(cameraStreamUrl => cameraActions.loadOpenEyeCameraStreamUrl({ cameraStreamUrl: cameraStreamUrl.content })),
            catchError(err => of(copActions.setError({ err })))
          );
      })
    )
  );

  getOpenEyeCameraImageUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cameraActions.getOpenEyeCameraImageUrl),
      switchMap(action => {
        return this.pipelineApiService
          .getOpenEyeCameraImageUrl(action.companyId, action.deviceId, action.cameraId, action.streamName, action.pipelineDefinitionUId)
          .pipe(
            map(cameraImageUrl => cameraActions.loadOpenEyeCameraImageUrl({ cameraImageUrl: cameraImageUrl.content })),
            catchError(err => of(copActions.setError({ err })))
          );
      })
    )
  );

  getVmsCameraMedia$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cameraActions.getVmsCameraMedia),
      switchMap(action => {
        this.mapDataWorkerService.sendVmsRequest(action.integrationKey, action.cameraId, action.mediaWidths, null, null);
        return [];
      })
    )
  );

  getVideoStreamUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cameraActions.getVideoStreamUrl),
      switchMap(action => {
        return this.videoStreamService.getVideoStreamUrl(action.request).pipe(
          map(videoStreamUrl => cameraActions.loadVideoStreamUrl({ videoStreamUrl })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  getLiveViewToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cameraActions.getLiveViewToken),
      switchMap(action => {
        return this.pipelineApiService.getLiveViewToken(action.pipelineDefinitionUId).pipe(
          map(response => cameraActions.loadLiveViewToken({ liveViewToken: response.token })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );
}
